import 'abortcontroller-polyfill/dist/polyfill-patch-fetch';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { Provider } from 'react-redux';
import Meta from '../components/meta';
import SiteShell from '../components/SiteShell';
import ErrorBoundary from '../lib/ErrorBoundary';
import { wrapper } from '../lib/store';
import { StringsProvider } from '../lib/StringsContext';
import { siteShell } from '../lib/siteShell';
import { setupGTM } from '../lib/utils/gtmTag';
import { VenueProvider } from '../lib/VenueContext';
import locales from '../locales';
import { useGetVenueByIdQuery } from '../services/s3Api';
import { initializeAppSession } from '../lib/sessionTracking';
import { initializeAttribution } from '../lib/attribution';
import { recordLandingEntry } from '../hooks/useCanGoBack';
import { useApiHandoff } from '../hooks/useApiHandoff';
import '../styles/calendar.css';
import '../styles/index.css';

// Create a new component to handle venue data fetching
const VenueDataFetcher = ({ children }) => {
  const router = useRouter();
  const venueId = router?.query?.venueId;

  // Without `skip`, RTK Query fires `venue/en/undefined.slim.4.json` on
  // every route that doesn't have a `[venueId]` segment (e.g. the
  // confirmation page). The S3 bucket returns 403 for that URL and we
  // pay for a wasted round-trip on every navigation. The 403 response
  // also can't be fed through `transformResponse` cleanly because it
  // dereferences `urls.booking`.
  const hasVenueId =
    typeof venueId === 'string' ? venueId.length > 0 : Array.isArray(venueId) && venueId.length > 0;

  const { data: venueData } = useGetVenueByIdQuery(
    {
      lang: 'en',
      venueId,
      slim: true,
    },
    { skip: !hasVenueId },
  );

  const now = new Date();

  const openingDate = venueData?.openingDate ? new Date(venueData.openingDate) : null;
  const closingDate = venueData?.closingDate ? new Date(venueData.closingDate) : null;

  const shouldNoIndex = (openingDate && now < openingDate) || (closingDate && now > closingDate);

  return (
    <>
      <Head>{shouldNoIndex && <meta name="robots" content="noindex, nofollow" />}</Head>
      {children}
    </>
  );
};

function MyApp({ Component, ...rest }) {
  const router = useRouter();
  const { store, props } = wrapper.useWrappedStore(rest);
  const [isNavigationComplete, setIsNavigationComplete] = useState(false);
  const [gtmInitialized, setGtmInitialized] = useState(false);
  useApiHandoff();

  const venueId = router?.query?.venueId;
  const countryCode = venueId?.toString()?.split('-')[0]?.toUpperCase();
  const shell = siteShell(router?.locale);

  useEffect(() => {
    const checkAndRedirect = async () => {
      if (window) {
        const currentLocale = window.location?.pathname?.split('/')[1];
        if (!locales.includes(currentLocale)) {
          try {
            const newLocale = locales.includes(window.navigator.language.substring(0, 2))
              ? window.navigator.language.substring(0, 2)
              : 'en';
            await router.replace(`/${newLocale}${router.asPath}`);
          } catch (error) {
            console.error('Navigation error:', error);
          }
        }
      }
      setIsNavigationComplete(true);
    };
    checkAndRedirect();
  }, [router.asPath]);

  useEffect(() => {
    if (!gtmInitialized && countryCode) {
      setupGTM(countryCode);
      setGtmInitialized(true);
    }
  }, [gtmInitialized, countryCode]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      recordLandingEntry();

      // Initialize session with fingerprinting
      initializeAppSession()
        .then((sessionData) => {
          console.log('App session initialized:', {
            sessionId: sessionData.sessionId,
            fingerprint: sessionData.fingerprint,
          });
        })
        .catch((error) => {
          console.error('Failed to initialize app session:', error);
        });

      // Initialize attribution tracking
      const attribution = initializeAttribution();
      console.log('Attribution initialized:', attribution);
    }
  }, []);

  useEffect(() => {
    if (!window.location.pathname.endsWith('/open-hours')) {
      const domain = window.location.hostname;
      const baseDomain = domain.split('.').slice(-2).join('.');
      const consentScript = document.createElement('script');
      consentScript.async = true;
      consentScript.src = `/${baseDomain}_consentmanager_script.js`;
      document.head.appendChild(consentScript);
    }
  }, []);

  return (
    <ErrorBoundary>
      <Provider store={store}>
        <VenueDataFetcher>
          <VenueProvider value={props?.pageProps?.venue}>
            <StringsProvider value={props?.pageProps?.strings}>
              <Meta />
              <Head>
                <meta name="viewport" content="width=device-width, initial-scale=1" />
                <title key="title">{shell.title}</title>
                <meta key="description" name="description" content={shell.description} />
                <meta key="og:site_name" property="og:site_name" content={shell.brand} />
                <meta key="og:title" property="og:title" content={shell.title} />
                <meta key="og:description" property="og:description" content={shell.description} />
              </Head>
              {/* The page waits for the language redirect above, so on the server
                  and until then the guest (and any scanner) gets the branded shell. */}
              {isNavigationComplete ? <Component {...props.pageProps} /> : <SiteShell locale={router?.locale} />}
            </StringsProvider>
          </VenueProvider>
        </VenueDataFetcher>
      </Provider>
    </ErrorBoundary>
  );
}

export default MyApp;
